import type {
  DealInsightsJobApi,
  UseDealInsightsJobApi,
} from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { defaultFetchError } from '@openx/components-deal/lib/utils/handleBqJobResponse.ts';
import type { BqJobResponseApi } from '@openx/types/bqJob.ts';
import type { DealInsightsApi } from '@openx/types/dealInsights.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  CreateDealInsightsJobDocument,
  type CreateDealInsightsJobMutationVariables,
} from '../../../../graphql/mutation/deals/CreateDealInsightsJob.generated.tsx';
import { CancelDealInsightsJobDocument } from '../../../../graphql/query/deals/CancelDealInsightsJob.generated.tsx';
import { GetDealInsightsJobDocument } from '../../../../graphql/query/deals/GetDealInsightsJob.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../../modules/graphql/types.ts';

// Plain requests: the job lifecycle (polling, cancellation, caching) belongs to `useDealInsightsSection`.
export const useDealInsightsJobApi: UseDealInsightsJobApi = () => {
  const queryClient = useQueryClient();

  return useMemo<DealInsightsJobApi>(
    () => ({
      createJob: async ({ account: _account, ...variables }) => {
        // `account` is not a declared variable of the mutation; Apollo used to strip it, plain transport would send it.
        const data = await getGraphQLRequestFromClient(queryClient)(
          CreateDealInsightsJobDocument,
          variables as CreateDealInsightsJobMutationVariables
        );
        const jobId = data.createDealInsightsJob?.job_id;
        if (!jobId) {
          throw new Error(defaultFetchError);
        }
        return jobId;
      },
      getJob: async jobId => {
        const data = await getGraphQLRequestFromClient(queryClient)(GetDealInsightsJobDocument, { job_id: jobId });
        return data.dealInsightsJob as unknown as BqJobResponseApi<DealInsightsApi>;
      },
      cancelJob: async jobId => {
        await getGraphQLRequestFromClient(queryClient)(CancelDealInsightsJobDocument, { jobId });
      },
    }),
    [queryClient]
  );
};
